import axios from 'axios';
import config from '../config/config';
import tokenManager from './tokenManager';
import { GoogleAuthProvider, signInWithPopup, getAuth } from 'firebase/auth';
import { initializeApp } from 'firebase/app';

const API_BASE_URL = config.API_URL;

console.log('API Service initialized with URL:', API_BASE_URL);

const API = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add request interceptor for logging
API.interceptors.request.use((config) => {
  console.log('Making request to:', config.url);
  return config;
}, (error) => {
  console.error('Request error:', error);
  return Promise.reject(error);
});

// Add response interceptor for logging
API.interceptors.response.use((response) => {
  console.log('Received response from:', response.config.url);
  return response;
}, (error) => {
  console.error('Response error:', error.response || error);
  return Promise.reject(error);
});

// Add this interceptor to include the token in all requests
API.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Add response interceptor for smart token refresh
API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      // Only refresh if the token manager says we should
      if (!tokenManager.shouldRefreshToken()) {
        console.log('🔑 Token appears valid despite 401, attempting direct retry');
        return Promise.reject(error);
      }
      
      // Check if we're already refreshing
      if (tokenManager.isCurrentlyRefreshing()) {
        // Wait for the current refresh to complete
        return new Promise((resolve, reject) => {
          tokenManager.addRefreshSubscriber((error, token) => {
            if (error) {
              reject(error);
            } else {
              originalRequest.headers['Authorization'] = `Bearer ${token}`;
              resolve(API(originalRequest));
            }
          });
        });
      }
      
      tokenManager.setRefreshingState(true);
      
      try {
        console.log('🔄 401 error, refreshing token');
        const refreshResponse = await refreshToken();
        const { token } = refreshResponse;
        
        localStorage.setItem('token', token);
        localStorage.setItem('userData', JSON.stringify(refreshResponse.user));
        API.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        originalRequest.headers['Authorization'] = `Bearer ${token}`;
        
        tokenManager.onRefreshComplete(null, token);
        tokenManager.setRefreshingState(false);
        
        return API(originalRequest);
      } catch (refreshError) {
        console.error('🔑 Token refresh failed:', refreshError);
        tokenManager.onRefreshComplete(refreshError, null);
        tokenManager.setRefreshingState(false);
        
        localStorage.removeItem('token');
        localStorage.removeItem('userRole');
        localStorage.removeItem('userData');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

export const signup = (userData) => API.post('/auth/signup', userData);

export const login = (email, password) => API.post('/auth/login', { email, password });

export const getPOIs = async () => {
  try {
    const response = await API.get('/general/pois');
    return response.data;
  } catch (error) {
    console.error('Error fetching POIs:', error);
    throw error;
  }
};

export const searchPOIs = (query) => API.get(`/general/pois?search=${query}`);
export const getPOIById = async (id) => {
  try {
    const response = await API.get(`/general/poi/${id}`);
    console.log('POI data fetched:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching POI by ID:', error);
    throw error;
  }
};
export const getAmenities = async () => {
  try {
    const response = await API.get('/general/amenities');
    return response.data;
  } catch (error) {
    console.error('Error fetching amenities:', error);
    throw error;
  }
};
export const getItineraries = async () => {
  try {
    const response = await API.get('/itineraries');
    return response.data;
  } catch (error) {
    console.error('Error fetching itineraries:', error);
    throw error;
  }
};
export const getPlaceToStayById = async (id) => {
  console.log(`Fetching place to stay with ID: ${id}`);
  try {
    const response = await API.get(`/general/places-to-stay/${id}`);
    console.log(`Place to stay data received:`, response.data);
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 404) {
      console.log(`Place to stay with ID ${id} not found`);
      return null;
    }
    console.error('Error fetching place to stay:', error);
    throw error;
  }
};
export const getPlaceToEatById = async (id) => {
  try {
    const response = await API.get(`/general/places-to-eat/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching place to eat:', error);
    throw error;
  }
};

export const addFavorite = (poiId) => API.post('/favorites', { poiId });
export const addPOIToTrip = async (tripId, poiId, day = 1, hour = 12, duration = 1) => {
  try {
    const response = await API.post(`/itineraries/${tripId}/pois`, { poiId, day, hour, duration });
    return response.data;
  } catch (error) {
    console.error('Error adding POI to trip:', error);
    throw error;
  }
};
export const createTrip = async (tripData) => {
  try {
    const response = await API.post('/itineraries', tripData);
    return response.data; // This should include the trip ID
  } catch (error) {
    console.error('Error creating trip:', error);
    throw error;
  }
};

export const getTrips = async () => {
  try {
    const response = await API.get('/itineraries');
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Error fetching trips:', error);
    throw error;
  }
};

export const getItineraryById = async (id) => {
  try {
    const response = await API.get(`/itineraries/${id}`);
    console.log('Itinerary response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching itinerary:', error.response || error);
    throw error;
  }
};

export const getPOIDetails = async (poiIds) => {
  try {
    const response = await API.post('/itineraries/poi-details', { poiIds });
    return response.data;
  } catch (error) {
    console.error('Error fetching POI details:', error);
    throw error;
  }
};

export const refreshToken = async () => {
  const token = localStorage.getItem('token');
  try {
    const response = await API.post('/auth/refresh-token', {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data; // This should include both the new token and user data
  } catch (error) {
    console.error('Error refreshing token:', error);
    throw error;
  }
};

export const getUserFavorites = async () => {
  try {
    const response = await API.get('/favorites');
    console.log('getUserFavorites response:', response);
    return response.data;
  } catch (error) {
    console.error('Error in getUserFavorites:', error);
    throw error;
  }
};

export const removeFavorite = (poiId) => API.delete(`/favorites/${poiId}`);

export const updateItinerary = async (id, changes) => {
  try {
    const response = await API.put(`/itineraries/${id}`, changes);
    return response.data;
  } catch (error) {
    console.error('Error updating itinerary:', error);
    throw error;
  }
};

export const getUserTrips = async () => {
  try {
    const response = await API.get('/itineraries');
    return response.data;
  } catch (error) {
    console.error('Error fetching user trips:', error);
    throw error;
  }
};

export const deleteItinerary = async (id) => {
  try {
    const response = await API.delete(`/itineraries/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting itinerary:', error);
    throw error;
  }
};

export const cloneItinerary = async (id) => {
  try {
    const response = await API.post(`/itineraries/${id}/clone`);
    return response.data;
  } catch (error) {
    console.error('Error cloning itinerary:', error);
    throw error;
  }
};

export const createCustomActivity = async (itineraryId, activityData) => {
  try {
    const response = await API.post(`/itineraries/${itineraryId}/custom-activity`, {
      ...activityData,
      order: 1 // Default order, can be adjusted if needed
    });
    return response.data;
  } catch (error) {
    console.error('Error creating custom activity:', error);
    throw error;
  }
};

export const getCustomActivities = async (itineraryId) => {
  try {
    const response = await API.get(`/itineraries/${itineraryId}/custom-activities`);
    return response.data;
  } catch (error) {
    console.error('Error fetching custom activities:', error);
    throw error;
  }
};

export const updateCustomActivity = async (itineraryId, activityId, updateData) => {
  try {
    const response = await API.put(`/itineraries/${itineraryId}/custom-activity/${activityId}`, updateData);
    return response.data;
  } catch (error) {
    console.error('Error updating custom activity:', error);
    throw error;
  }
};

export const deleteCustomActivity = async (itineraryId, activityId) => {
  try {
    const response = await API.delete(`/itineraries/${itineraryId}/custom-activity/${activityId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting custom activity:', error);
    throw error;
  }
};

export const cloneCustomActivity = async (itineraryId, activityId) => {
  try {
    const response = await API.post(`/itineraries/${itineraryId}/custom-activities/${activityId}/clone`);
    return response.data;
  } catch (error) {
    console.error('Error cloning custom activity:', error);
    throw error;
  }
};

export const updateItineraryPOI = async (itineraryId, poiId, updateData) => {
  try {
    console.log('Updating POI:', { itineraryId, poiId, updateData });
    const response = await API.put(`/itineraries/${itineraryId}/pois/${poiId}`, updateData);
    console.log('Update POI response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error updating itinerary POI:', error.response || error);
    throw error;
  }
};

export const createTripWithPOI = async (tripData) => {
  try {
    const response = await API.post('/itineraries', tripData);
    return response.data;
  } catch (error) {
    console.error('Error creating trip with POI:', error);
    throw error;
  }
};

export const getAllPublicItineraries = async () => {
  try {
    const response = await API.get('/public-itineraries');
    return response.data;
  } catch (error) {
    console.error('Error fetching public itineraries:', error);
    throw error;
  }
};

export const getPublicItineraryById = async (id) => {
  try {
    const response = await API.get(`/public-itineraries/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching public itinerary:', error);
    throw error;
  }
};

export const bookPublicItinerary = async (id, startDate, endDate) => {
  try {
    const response = await API.post(`/public-itineraries/${id}/book`, { startDate, endDate });
    return response.data; // This should be the newly created itinerary
  } catch (error) {
    console.error('Error booking public itinerary:', error);
    throw error;
  }
};

export const clonePOI = async (itineraryId, poiId) => {
  try {
    console.log('Cloning POI:', { itineraryId, poiId });
    const response = await API.post(`/itineraries/${itineraryId}/pois/${poiId}/clone`);
    console.log('Clone POI response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error cloning POI:', error.response || error);
    throw error;
  }
};

export const getWeather = async () => {
  try {
    console.log('Making request to /weather');
    const response = await API.get('/weather'); // Remove 'api' from the path
    console.log('Weather API response:', response);
    return response.data;
  } catch (error) {
    console.error('Weather API error:', error.response || error);
    throw error;
  }
};

// Add this to your existing api.js file
export const getExchangeRates = async () => {
  try {
    const response = await API.get('/exchange-rates');
    return response.data;
  } catch (error) {
    console.error('Exchange rates API error:', error.response || error);
    throw error;
  }
};

// Add these new endpoints
export const createBooking = async (bookingData) => {
  try {
    const response = await API.post('/bookings/create', bookingData);
    return response.data;
  } catch (error) {
    console.error('Error creating booking:', error);
    throw error;
  }
};

export const confirmBooking = async (paypalOrderId, bookingDetails) => {
  try {
    const response = await API.post('/bookings/confirm', {
      paypalOrderId,
      bookingDetails
    });
    return response.data;
  } catch (error) {
    console.error('Error confirming booking:', error);
    throw error;
  }
};

// Add these new functions to your existing api.js

export const getUserProfile = async () => {
  try {
    const response = await API.get('/users/profile');
    return response.data;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    throw error;
  }
};

export const updateUserProfile = async (profileData) => {
  try {
    const response = await API.put('/users/profile', profileData);
    return response.data;
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

export const getBookingHistory = async () => {
  try {
    const response = await API.get('/bookings/history');
    return response.data;
  } catch (error) {
    console.error('Error fetching booking history:', error);
    throw error;
  }
};

export const getBookingDetails = async (bookingId) => {
  try {
    const response = await API.get(`/bookings/${bookingId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching booking details:', error);
    throw error;
  }
};

export const socialLogin = async (provider, token) => {
  try {
    if (!firebaseApp) {
      throw new Error('Social login is not configured');
    }

    let socialToken;
    let userData;

    switch (provider) {
      case 'google':
        const auth = getAuth(firebaseApp);
        const googleProvider = new GoogleAuthProvider();
        const result = await signInWithPopup(auth, googleProvider);
        socialToken = await result.user.getIdToken();
        userData = {
          email: result.user.email,
          firstName: result.user.displayName?.split(' ')[0] || '',
          lastName: result.user.displayName?.split(' ').slice(1).join(' ') || ''
        };
        break;

      case 'apple':
        // Apple Sign In implementation will go here
        // We'll need to add Apple Sign In SDK
        throw new Error('Apple sign in not yet implemented');

      default:
        throw new Error('Invalid provider');
    }

    // Send to backend
    const response = await axios.post(`${API_BASE_URL}/auth/social-login`, {
      provider,
      token: socialToken,
      userData
    });

    return response.data;
  } catch (error) {
    console.error('Social login error:', error);
    throw error;
  }
};

// Add these functions to your existing api.js file

export const getBudgetLimits = async (itineraryId) => {
  const response = await API.get(`/api/itineraries/${itineraryId}/budget-limits`);
  return response.data;
};

export const createBudgetLimit = async (itineraryId, limitData) => {
  const response = await API.post(`/api/itineraries/${itineraryId}/budget-limits`, limitData);
  return response.data;
};

export const deleteBudgetLimit = async (limitId) => {
  const response = await API.delete(`/api/budget-limits/${limitId}`);
  return response.data;
};

export const getChecklistItems = async (itineraryId) => {
  const response = await API.get(`/api/itineraries/${itineraryId}/checklist`);
  return response.data;
};

export const saveChecklistItems = async (itineraryId, items) => {
  const response = await API.post(`/api/itineraries/${itineraryId}/checklist`, { items });
  return response.data;
};

export const deleteChecklistItem = async (itemId) => {
  const response = await API.delete(`/api/checklist/${itemId}`);
  return response.data;
};

export const changePassword = async (passwordData) => {
  try {
    const response = await API.post('/users/change-password', passwordData);
    return response.data;
  } catch (error) {
    console.error('Error changing password:', error);
    throw error;
  }
};

// Add this function to your existing api.js file
export const updateSubscriptionStatus = async (data) => {
  try {
    const response = await API.post('/users/subscription', data);
    return response.data;
  } catch (error) {
    console.error('Error updating subscription:', error);
    throw error;
  }
};

export const getSubscriptionBillings = async (subscriptionId) => {
  try {
    const response = await API.get(`/users/subscription/${subscriptionId}/billings`);
    return response.data;
  } catch (error) {
    console.error('Error fetching subscription billings:', error);
    throw error;
  }
};

export const cancelSubscription = async (subscriptionId) => {
  try {
    const response = await API.post(`/users/subscription/${subscriptionId}/cancel`);
    return response.data;
  } catch (error) {
    console.error('Error cancelling subscription:', error);
    throw error;
  }
};

export const resumeSubscription = async (subscriptionId) => {
  try {
    const response = await API.post(`/users/subscription/${subscriptionId}/resume`);
    return response.data;
  } catch (error) {
    console.error('Error resuming subscription:', error);
    throw error;
  }
};

// Initialize Firebase (add this at the top of the file)
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Initialize Firebase only if credentials exist
let firebaseApp;
try {
  if (Object.values(firebaseConfig).every(value => value)) {
    firebaseApp = initializeApp(firebaseConfig);
  }
} catch (error) {
  console.warn('Firebase not initialized:', error);
}

// Tour endpoints
export const getPublicTours = async () => {
  try {
    const response = await API.get('/general/public-tours');
    return response.data;
  } catch (error) {
    console.error('Error fetching public tours:', error);
    throw error;
  }
};

export const getTourById = async (id) => {
  try {
    const response = await API.get(`/general/tours/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching tour:', error);
    throw error;
  }
};

export const bookTour = async (bookingData) => {
  try {
    const response = await API.post(`/general/tours/${bookingData.tourId}/book`, {
      tourDateId: bookingData.tourDateId,
      paypalOrderId: bookingData.paypalOrderId
    });
    return response.data;
  } catch (error) {
    console.error('Error booking tour:', error);
    throw error;
  }
};

export const getOperatorProfile = async (operatorId) => {
  try {
    const response = await API.get(`/operators/${operatorId}/profile`);
    return response.data;
  } catch (error) {
    console.error('Error fetching operator profile:', error);
    throw error;
  }
};

export const getOperatorTours = async (operatorId) => {
  try {
    const response = await API.get(`/operators/${operatorId}/tours`);
    return response.data;
  } catch (error) {
    console.error('Error fetching operator tours:', error);
    throw error;
  }
};
